import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaHeart, FaMusic, FaDownload, FaPlay, FaTrash, FaHdd } from 'react-icons/fa';
import AlbumCard from '../components/AlbumCard';
import { getAllDownloaded, removeDownloadedTrack, getStorageUsage, clearAllDownloads } from '../utils/offlineManager';
import '../styles/LibraryPage.css';

function LibraryPage({ playTrack, addToQueue }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('favorites');
  const [downloads, setDownloads] = useState([]);
  const [loadingDl, setLoadingDl] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);

  useEffect(() => {
    loadFavorites();
  }, []);

  useEffect(() => {
    if (activeTab === 'downloads') {
      loadDownloads();
    }
  }, [activeTab]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/playlists/favorites/list');
      setFavorites(response.data || []);
    } catch (error) {
      console.error('Erreur chargement favoris:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDownloads = async () => {
    try {
      setLoadingDl(true);
      const tracks = await getAllDownloaded();
      setDownloads(tracks);
      const storage = await getStorageUsage();
      setStorageInfo(storage);
    } catch (error) {
      console.error('Erreur chargement téléchargements:', error);
    } finally {
      setLoadingDl(false);
    }
  };

  const handleFavoriteChange = () => {
    loadFavorites();
  };

  const handleRemoveDownload = async (track) => {
    try {
      await removeDownloadedTrack(track);
      setDownloads(prev => prev.filter(t => t.id !== track.id));
      const storage = await getStorageUsage();
      setStorageInfo(storage);
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Supprimer tous les téléchargements hors-ligne ?')) return;
    try {
      await clearAllDownloads();
      setDownloads([]);
      const storage = await getStorageUsage();
      setStorageInfo(storage);
    } catch (error) {
      console.error('Erreur suppression totale:', error);
    }
  };

  const handlePlayDownloaded = (track) => {
    const albumTracks = downloads
      .filter(t => t.album_id === track.album_id)
      .sort((a, b) => (a.disc_number - b.disc_number) || (a.track_number - b.track_number));
    playTrack(track, albumTracks);
  };

  const formatDuration = (s) => {
    if (!s) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const getCoverUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `https://disney-music.gpworld.fr${url.startsWith('/') ? url : '/' + url}`;
  };

  // Grouper les téléchargements par album
  const groupedDownloads = downloads.reduce((acc, track) => {
    const key = track.album_id || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        album_id: track.album_id,
        album_title: track.album_title || 'Album inconnu',
        album_artist: track.album_artist || '',
        cover_url: track.cover_url,
        tracks: []
      };
    }
    acc[key].tracks.push(track);
    return acc;
  }, {});

  // Trier les pistes dans chaque album
  Object.values(groupedDownloads).forEach(album => {
    album.tracks.sort((a, b) => (a.disc_number - b.disc_number) || (a.track_number - b.track_number));
  });

  return (
    <div className="library-page">
      <div className="library-header">
        <h1>
          <FaMusic /> Ma Bibliothèque
        </h1>
        <p>Retrouvez vos favoris et téléchargements</p>
      </div>

      <div className="library-tabs">
        <button
          className={`library-tab ${activeTab === 'favorites' ? 'active' : ''}`}
          onClick={() => setActiveTab('favorites')}
        >
          <FaHeart /> Favoris ({favorites.length})
        </button>
        <button
          className={`library-tab ${activeTab === 'downloads' ? 'active' : ''}`}
          onClick={() => setActiveTab('downloads')}
        >
          <FaDownload /> Hors-ligne ({downloads.length})
        </button>
      </div>

      <div className="library-content">
        {activeTab === 'favorites' && (
          <>
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Chargement...</p>
              </div>
            ) : favorites.length === 0 ? (
              <div className="empty-state">
                <FaHeart className="empty-icon" />
                <h3>Aucun favori</h3>
                <p>Ajoutez des albums à vos favoris pour les retrouver ici</p>
              </div>
            ) : (
              <div className="albums-grid">
                {favorites.map(album => (
                  <AlbumCard
                    key={album.id}
                    album={album}
                    playTrack={playTrack} addToQueue={addToQueue}
                    onFavoriteChange={handleFavoriteChange}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'downloads' && (
          <>
            {storageInfo && (
              <div className="storage-info">
                <FaHdd />
                <span>{storageInfo.usedMB} Mo utilisés</span>
                {downloads.length > 0 && (
                  <button className="clear-all-btn" onClick={handleClearAll}>
                    <FaTrash /> Tout supprimer
                  </button>
                )}
              </div>
            )}

            {loadingDl ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Chargement...</p>
              </div>
            ) : downloads.length === 0 ? (
              <div className="empty-state">
                <FaDownload className="empty-icon" />
                <h3>Aucun téléchargement</h3>
                <p>Téléchargez des pistes depuis les albums pour les écouter hors-ligne</p>
              </div>
            ) : (
              <div className="downloads-list">
                {Object.values(groupedDownloads).map(album => (
                  <div key={album.album_id} className="dl-album-group">
                    <div className="dl-album-header">
                      {album.cover_url && (
                        <img
                          src={getCoverUrl(album.cover_url)}
                          alt={album.album_title}
                          className="dl-album-cover"
                        />
                      )}
                      <div className="dl-album-info">
                        <h3>{album.album_title}</h3>
                        <p>{album.album_artist} · {album.tracks.length} piste{album.tracks.length > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="dl-tracks">
                      {album.tracks.map(track => (
                        <div key={track.id} className="dl-track-item">
                          <div
                            className="dl-track-main"
                            onClick={() => handlePlayDownloaded(track)}
                          >
                            <span className="dl-track-num">{track.track_number}</span>
                            <div className="dl-track-info">
                              <span className="dl-track-title">{track.title}</span>
                              <span className="dl-track-artist">{track.artist}</span>
                            </div>
                            <span className="dl-track-duration">{formatDuration(track.duration)}</span>
                          </div>
                          <div className="dl-track-actions">
                            <button
                              className="dl-track-play"
                              onClick={() => handlePlayDownloaded(track)}
                              title="Lire"
                            >
                              <FaPlay />
                            </button>
                            <button
                              className="dl-track-remove"
                              onClick={() => handleRemoveDownload(track)}
                              title="Supprimer du hors-ligne"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default LibraryPage;
